/**
 * Goals management page.
 *
 * Lists all goals, each showing a mini trend chart tracking the metric
 * against the goal line within the scoped portfolio group. Each card
 * supports toggling benchmark comparison lines.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, Target, Pencil, Trash2, BarChart3, ChevronDown, ChevronRight, Filter, Sparkles } from 'lucide-react'
import { execAPI } from '../services/exec'
import type { Goal, MeasureDefinition, MeasuresRegistry } from '../types/exec'
import GoalEditorFull from '../components/exec/GoalEditorFull'
import GoalTrackingChart from '../components/exec/GoalTrackingChart'
import GoalBullet from '../components/exec/GoalBullet'
import GoalStatusChip from '../components/exec/GoalStatusChip'
import { useBenchmarkQueries } from '../components/exec/generic/useBenchmarkQueries'

/**
 * Starter goal templates — surfaced when the user has no goals yet (Phase D).
 *
 * Clicking "Use this" opens GoalEditorFull pre-filled with the measure +
 * target + direction; the user still picks the portfolio group and saves.
 * Both UX (orientation for first-time users) and data (something to render
 * the dashboard's goal-line + status chip overlays against during testing).
 */
interface StarterGoal {
  name: string
  description: string
  measure: string
  target_value: { value: number; direction: 'higher_is_better' | 'lower_is_better'; unit: 'percent' | 'days' | 'dollars' | 'number' }
  /** Default target date (YYYY-MM-DD); blank lets the user pick. */
  effective_to?: string
}

const STARTER_GOALS: StarterGoal[] = [
  {
    name: 'Full & Open ≥ 60% by FY27',
    description: 'OSD-aligned target — 60% of FAR awards should be full and open by end of FY27.',
    measure: 'competition_health',
    target_value: { value: 60, direction: 'higher_is_better', unit: 'percent' },
    effective_to: '2027-09-30',
  },
  {
    name: 'Small Business $ ≥ 25% by FY27',
    description: 'SBA Goaling target — 25% of obligated dollars to small businesses.',
    measure: 'small_biz_engagement',
    target_value: { value: 25, direction: 'higher_is_better', unit: 'percent' },
    effective_to: '2027-09-30',
  },
  {
    name: 'Speed to Contract ≤ 90 days by FY27',
    description: 'Median solicitation-to-award day count under 90 days.',
    measure: 'speed_to_contract',
    target_value: { value: 90, direction: 'lower_is_better', unit: 'days' },
    effective_to: '2027-09-30',
  },
  {
    name: 'OT Awards ≥ 15% of awards by FY27',
    description: 'Push commercial-tech / non-FAR pathways — 15% of awards via Other Transactions.',
    measure: 'ot_prevalence',
    target_value: { value: 15, direction: 'higher_is_better', unit: 'percent' },
    effective_to: '2027-09-30',
  },
]

function formatTarget(goal: Goal) {
  const { value, direction, unit } = goal.target_value
  const prefix = direction === 'lower_is_better' ? '\u2264' : '\u2265'
  if (unit === 'days') return `${prefix} ${value} days`
  if (unit === 'percent') return `${prefix} ${value}%`
  if (unit === 'dollars') return `${prefix} $${value.toLocaleString()}`
  return `${prefix} ${value}`
}

/** Per-goal card — extracted so it can own benchmark hooks. */
function GoalCard({
  goal,
  measureDef,
  onEdit,
  onDelete,
  deleteConfirm,
  onDeleteConfirm,
}: {
  goal: Goal
  measureDef: MeasureDefinition | undefined
  onEdit: () => void
  onDelete: () => void
  deleteConfirm: boolean
  onDeleteConfirm: (id: string | null) => void
}) {
  const [showBenchmarks, setShowBenchmarks] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [latestValue, setLatestValue] = useState<number | null>(null)
  const [priorValue, setPriorValue] = useState<number | null>(null)
  const hasBenchmarks = goal.benchmark_sources && goal.benchmark_sources.length > 0

  const benchmarkResult = useBenchmarkQueries(
    showBenchmarks && hasBenchmarks ? goal.benchmark_sources : [],
    { group_by: 'fiscal_year' },
  )

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Goal header */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {goal.name || measureDef?.label || goal.measure}
            </h3>
            <span className="text-xs font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
              {formatTarget(goal)}
            </span>
            <GoalStatusChip goal={goal} latestValue={latestValue} priorValue={priorValue} />
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            {goal.portfolio_group_name && (
              <span>Portfolio: {goal.portfolio_group_name}</span>
            )}
            {goal.office_name && (
              <span>Office: {goal.office_name}</span>
            )}
            {measureDef && (
              <span className="text-gray-400">{measureDef.category}: {measureDef.label}</span>
            )}
            {goal.effective_to && (
              <span>Target: {goal.effective_to}</span>
            )}
          </div>
          {goal.description && (
            <p className="text-xs text-gray-500 mt-1">{goal.description}</p>
          )}
          {/* Benchmark labels when visible */}
          {showBenchmarks && hasBenchmarks && (
            <div className="flex items-center gap-2 mt-1.5">
              {goal.benchmark_sources.map((bm) => (
                <span
                  key={bm.id}
                  className="inline-flex items-center gap-1 text-[10px] text-gray-500"
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: bm.color }} />
                  {bm.label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 ml-3 shrink-0">
          {/* Benchmark toggle */}
          <button
            onClick={() => setShowBenchmarks((v) => !v)}
            className={`p-1.5 rounded transition-colors ${
              showBenchmarks
                ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-violet-600'
            }`}
            title={hasBenchmarks ? (showBenchmarks ? 'Hide benchmarks' : 'Show benchmarks') : 'No benchmarks configured — edit goal to add'}
          >
            <BarChart3 size={14} />
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-blue-600"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          {deleteConfirm ? (
            <div className="flex items-center gap-1 text-xs">
              <button onClick={onDelete} className="px-1.5 py-0.5 rounded bg-red-500 text-white hover:bg-red-600">Delete</button>
              <button onClick={() => onDeleteConfirm(null)} className="px-1.5 py-0.5 rounded text-gray-500">Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => onDeleteConfirm(goal.id)}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-red-500"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Bullet (D1 default) — quick "are we on track?" read */}
      {!showHistory && (
        <div className="h-32 px-2 py-2">
          <GoalBullet
            goal={goal}
            measureDef={measureDef}
            onResolved={(latest, prior) => {
              setLatestValue(latest)
              setPriorValue(prior)
            }}
          />
        </div>
      )}

      {/* Trend history — toggleable */}
      {showHistory && (
        <div className="h-48 px-2 py-2">
          <GoalTrackingChart
            goal={goal}
            measureDef={measureDef}
            benchmarkSeries={showBenchmarks ? benchmarkResult.series : undefined}
          />
        </div>
      )}

      {/* Expand history toggle */}
      <div className="flex items-center justify-between px-4 pb-2">
        <button
          onClick={() => setShowHistory((v) => !v)}
          className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          {showHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {showHistory ? 'Hide history' : 'Expand history'}
        </button>
        {showBenchmarks && !hasBenchmarks && (
          <p className="text-[10px] text-gray-400">
            No benchmarks configured. Edit this goal to add benchmark comparisons.
          </p>
        )}
      </div>
    </div>
  )
}

type SortKey = 'deadline' | 'created' | 'measure' | 'portfolio'

export default function Goals() {
  const [goals, setGoals] = useState<Goal[]>([])
  const [measures, setMeasures] = useState<MeasuresRegistry['measures']>({})
  const [loading, setLoading] = useState(true)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null)
  const [presetForEditor, setPresetForEditor] = useState<Partial<Goal> | undefined>(undefined)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  // Filter + sort state (Phase D4)
  const [filterMeasure, setFilterMeasure] = useState<string>('')
  const [filterPortfolio, setFilterPortfolio] = useState<string>('')
  const [sortKey, setSortKey] = useState<SortKey>('deadline')

  const loadData = useCallback(async () => {
    try {
      const [goalsData, registry] = await Promise.all([
        execAPI.getGoals(),
        execAPI.getMeasuresRegistry(),
      ])
      setGoals(goalsData)
      setMeasures(registry.measures)
    } catch (err) {
      console.error('Failed to load goals:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const handleDelete = async (id: string) => {
    try {
      await execAPI.deleteGoal(id)
      setDeleteConfirm(null)
      loadData()
    } catch (err) {
      console.error('Failed to delete goal:', err)
    }
  }

  // Distinct values for the filter dropdowns
  const measureOptions = useMemo(() => {
    const set = new Set<string>()
    for (const g of goals) set.add(g.measure)
    return Array.from(set).sort()
  }, [goals])

  const portfolioOptions = useMemo(() => {
    const map = new Map<string, string>() // portfolio_id → name
    for (const g of goals) {
      if (g.portfolio_group) {
        map.set(g.portfolio_group, g.portfolio_group_name || g.portfolio_group)
      }
    }
    return Array.from(map.entries())
  }, [goals])

  // Visible goals after filter + sort
  const visibleGoals = useMemo(() => {
    let result = goals
    if (filterMeasure) result = result.filter((g) => g.measure === filterMeasure)
    if (filterPortfolio) result = result.filter((g) => g.portfolio_group === filterPortfolio)

    const sorted = [...result]
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'deadline': {
          // Soonest first; nulls last
          const da = a.effective_to ? new Date(a.effective_to).getTime() : Number.POSITIVE_INFINITY
          const db = b.effective_to ? new Date(b.effective_to).getTime() : Number.POSITIVE_INFINITY
          return da - db
        }
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'measure':
          return a.measure.localeCompare(b.measure)
        case 'portfolio':
          return (a.portfolio_group_name || '').localeCompare(b.portfolio_group_name || '')
        default:
          return 0
      }
    })
    return sorted
  }, [goals, filterMeasure, filterPortfolio, sortKey])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Target size={20} className="text-blue-500" />
            Performance Goals
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Set targets for your portfolio and track progress over time.
          </p>
        </div>
        <button
          onClick={() => { setEditingGoal(null); setEditorOpen(true) }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          New Goal
        </button>
      </div>

      {/* Filter + sort controls (Phase D4) */}
      {goals.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Filter size={12} />
            Filter:
          </div>
          <select
            value={filterMeasure}
            onChange={(e) => setFilterMeasure(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            <option value="">All measures</option>
            {measureOptions.map((m) => (
              <option key={m} value={m}>{measures[m]?.label ?? m}</option>
            ))}
          </select>
          <select
            value={filterPortfolio}
            onChange={(e) => setFilterPortfolio(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            <option value="">All portfolios</option>
            {portfolioOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <div className="text-xs text-gray-500 ml-2">Sort:</div>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            <option value="deadline">Deadline (soonest first)</option>
            <option value="created">Recently created</option>
            <option value="measure">Measure (A–Z)</option>
            <option value="portfolio">Portfolio (A–Z)</option>
          </select>
          <span className="text-xs text-gray-400 ml-auto">
            {visibleGoals.length} of {goals.length}
          </span>
        </div>
      )}

      {/* Starter goal templates (Phase D) — visible only when goals is empty */}
      {goals.length === 0 && (
        <div className="mb-6 p-4 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/20">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Try a starter goal</h3>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Pick a sample target to see goal-tracking in action — bullet, status chip, and goal-line on backing widgets. You can edit the values, change the deadline, and pick your portfolio scope before saving.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {STARTER_GOALS.map((s) => (
              <button
                key={s.name}
                onClick={() => {
                  setEditingGoal(null)
                  setPresetForEditor({
                    name: s.name,
                    description: s.description,
                    measure: s.measure,
                    target_value: s.target_value,
                    effective_to: s.effective_to,
                  } as Partial<Goal>)
                  setEditorOpen(true)
                }}
                className="text-left p-3 rounded-lg border border-blue-100 dark:border-blue-800 bg-white dark:bg-gray-900 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{s.name}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{s.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Goals list with tracking charts */}
      {goals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <Target size={36} className="mb-2 opacity-40" />
          <p className="text-xs">Or build a custom goal from scratch with the "New Goal" button above.</p>
        </div>
      ) : visibleGoals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Filter size={32} className="mb-2 opacity-40" />
          <p className="text-sm">No goals match the current filter.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleGoals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              measureDef={measures[goal.measure]}
              onEdit={() => { setEditingGoal(goal); setEditorOpen(true) }}
              onDelete={() => handleDelete(goal.id)}
              deleteConfirm={deleteConfirm === goal.id}
              onDeleteConfirm={setDeleteConfirm}
            />
          ))}
        </div>
      )}

      {/* Goal editor modal */}
      {editorOpen && (
        <GoalEditorFull
          goal={editingGoal}
          measures={measures}
          presetGoal={editingGoal ? undefined : presetForEditor}
          onClose={() => {
            setEditorOpen(false)
            setEditingGoal(null)
            setPresetForEditor(undefined)
            loadData()
          }}
        />
      )}
    </div>
  )
}
